import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';

import { IdeaGenStateService } from '../../idea-gen-state.service';
import { IdeaSnapshotComponent } from '../idea-snapshot/idea-snapshot.component';
import { MainNavbarComponent } from '../../../../core/components/main-navbar/main-navbar.component';
import { McapSliderComponent } from 'src/app/shared/components/mcap-slider/mcap-slider.component';
import { UniverseSelectorComponent } from 'src/app/shared/components/universe-selector/universe-selector.component';
import { REGION_CODES } from '../../../../../../public/country-region-codes';

@Component({
  selector: 'app-idea-generation-home',
  standalone: true,
  imports: [CommonModule, FormsModule, MainNavbarComponent, IdeaSnapshotComponent],
  templateUrl: './idea-generation-home.component.html',
  styleUrl: './idea-generation-home.component.css',
})
export class IdeaGenerationHomeComponent implements OnInit {
  endDate: string = '';

  countriesControl = new FormControl<string[]>([]);
  expandedRegions: Set<string> = new Set();
  countryFilterOptions = REGION_CODES;

  disableButtons$: Observable<boolean> = this.stateService.disableButtons$;

  min_market_cap: number | null = null;
  max_market_cap: number | null = null;
  private_company = true;
  public_company = true;

  constructor(private stateService: IdeaGenStateService, private dialog: MatDialog) {
    const today = new Date();
    this.endDate = today.toISOString().split('T')[0];
  }

  ngOnInit(): void {
    this.handleSubmit();
  }

  getToday(): string {
    return new Date().toISOString().split('T')[0];
  }

  handleSubmit(): void {
    this.stateService.setQuery({
      date_end: this.endDate || null,
    });
  }

  openMcapDialog(): void {
    const dialogRef = this.dialog.open(McapSliderComponent, {
      data: { min_market_cap: this.min_market_cap, max_market_cap: this.max_market_cap },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.min_market_cap = result.min;
        this.max_market_cap = result.max;
      }
    });
  }

  openUniverseSelector(): void {
    const dialogRef = this.dialog.open(UniverseSelectorComponent, {
      data: {
        min_mcap: this.min_market_cap,
        max_mcap: this.max_market_cap,
        include_public: this.public_company,
        include_private: this.private_company,
      },
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;
      this.private_company = result.isPrivate;
      this.public_company = result.isPublic;
      this.max_market_cap = result.max;
      this.min_market_cap = result.min;
    });
  }

  toggleExpand(region: string): void {
    if (this.expandedRegions.has(region)) {
      this.expandedRegions.delete(region);
    } else {
      this.expandedRegions.add(region);
    }
  }

  isExpanded(region: string): boolean {
    return this.expandedRegions.has(region);
  }

  toggleRegionSelection(region: any): void {
    const regionCountryCodes = region.countries.map((c: any) => c.code);
    const current = this.countriesControl.value ?? [];
    const allSelected = regionCountryCodes.every((code: string) => current.includes(code));
    if (allSelected) {
      this.countriesControl.setValue(current.filter((v: string) => !regionCountryCodes.includes(v)));
    } else {
      this.countriesControl.setValue(Array.from(new Set([...current, ...regionCountryCodes])));
    }
  }

  toggleCountrySelection(countryCode: string, _region: any): void {
    const current = this.countriesControl.value ?? [];
    if (current.includes(countryCode)) {
      this.countriesControl.setValue(current.filter((v: string) => v !== countryCode));
    } else {
      this.countriesControl.setValue([...current, countryCode]);
    }
  }

  isRegionFullySelected(region: any): boolean {
    const regionCountryCodes = region.countries.map((c: any) => c.code);
    const selectedCodes = this.countriesControl.value ?? [];
    return regionCountryCodes.every((code: string) => selectedCodes.includes(code));
  }

  formatMarketCap(value: number | null): string {
    if (!value) return '0';
    if (value >= 1e12) {
      const t = value / 1e12;
      return (t % 1 === 0 ? t.toString() : t.toFixed(2)) + 'T (USD)';
    }
    if (value >= 1e9) {
      const b = value / 1e9;
      return (b % 1 === 0 ? b.toString() : b.toFixed(2)) + 'B (USD)';
    }
    if (value >= 1e6) {
      const m = value / 1e6;
      return (m % 1 === 0 ? m.toString() : m.toFixed(2)) + 'M (USD)';
    }
    return value.toString();
  }

  getPeerGroupSelectionText(): string {
    const selections: string[] = [];
    if (this.public_company) selections.push('Public');
    if (this.private_company) selections.push('Private');
    if (this.public_company && (this.min_market_cap || this.max_market_cap)) {
      const minText = this.min_market_cap ? this.formatMarketCap(this.min_market_cap) : '';
      const maxText = this.max_market_cap ? this.formatMarketCap(this.max_market_cap) : '';
      if (minText && maxText) selections.push(`${minText}-${maxText}`);
      else if (minText) selections.push(`>${minText}`);
      else if (maxText) selections.push(`<${maxText}`);
    }
    return selections.length > 0 ? selections.join(', ') : '';
  }

  hasPeerGroupSelection(): boolean {
    return this.public_company || this.private_company;
  }
}
